import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useEvent } from '../hooks/useEvents'
import { useMe } from '../hooks/useMe'
import { useLivePulse } from '../hooks/useLivePulse'
import { Icon } from '../components/Icon'
import { ResourcesPanel } from '../components/ResourcesPanel'
import { AlarmModal } from '../components/live/AlarmModal'
import { BroadcastModal } from '../components/live/BroadcastModal'
import { LiveManagerOverview } from '../components/live/LiveManagerOverview'
import { LiveMyDuties } from '../components/live/LiveMyDuties'
import { PersonDutyModal } from '../components/live/PersonDutyModal'
import type { LiveShift } from '../types'

// LiveDayView — full-screen dark-theme operations dashboard (Live View
// PR L3 shell, PR L4 polish). Mounted outside the AppShell so the
// .day-view-top header sits flush against the viewport edge.
//
// L4 additions on top of L3:
//   - Calendar date stamp in header (font-mono "Tuesday, 13 May" style,
//     between View toggle and Resources button per prototype L2386).
//   - 1s wall-clock ticker rendered next to the date stamp.
//   - Connectivity dot state machine — green pulsing < 60s, amber
//     60–180s, red > 180s or fetch error. Sits next to "Updated Ns
//     ago". Distinct from the always-red .live-pulse-circle that
//     signals "this is the live view".
//   - Lost-connection banner across the top of the body when the
//     pulse stales past 180s or errors. Includes a Retry button that
//     fires refetch() on the pulse hook.
//   - BroadcastModal wired to the manager-only header button.
//   - PersonDutyModal opens when a Who's On row is clicked.
//   - LiveMyDuties replaces the L3 placeholder.
//   - Mobile/Desktop CSS swap via data-phone-view on the shell;
//     ResourcesPanel FAB visible only in phone-view mode.

type ViewMode = 'overview' | 'mine'

type ConnState = 'ok' | 'stale' | 'error'

function deriveConnState(secondsSinceUpdate: number | null, isError: boolean): ConnState {
  if (isError) return 'error'
  if (secondsSinceUpdate == null) return 'ok'
  if (secondsSinceUpdate >= 180) return 'error'
  if (secondsSinceUpdate >= 60) return 'stale'
  return 'ok'
}

export function LiveDayView() {
  const { eventId: idParam } = useParams<{ eventId: string }>()
  const eventId = Number(idParam)
  const validId = Number.isInteger(eventId) && eventId > 0

  const navigate = useNavigate()
  const { data: event, isLoading: eventLoading, error: eventError } = useEvent(validId ? eventId : 0)
  const { data: me } = useMe()
  const pulse = useLivePulse(validId ? eventId : 0)

  const [view, setView] = useState<ViewMode>('overview')
  const [dayIdx, setDayIdx] = useState<number | null>(null)
  const [phoneView, setPhoneView] = useState(false)
  const [showAlarm, setShowAlarm] = useState(false)
  const [showBroadcast, setShowBroadcast] = useState(false)
  const [showResources, setShowResources] = useState(false)
  const [viewingShift, setViewingShift] = useState<LiveShift | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [wallClock, setWallClock] = useState(() => new Date())

  // 1s wall-clock ticker — refreshes every second so the header time
  // updates without waiting on the next pulse poll.
  useEffect(() => {
    const id = window.setInterval(() => setWallClock(new Date()), 1000)
    return () => window.clearInterval(id)
  }, [])

  // Toast auto-dismiss. New toasts replace older ones.
  useEffect(() => {
    if (toast == null) return
    const id = window.setTimeout(() => setToast(null), 3000)
    return () => window.clearTimeout(id)
  }, [toast])

  // Seed dayIdx from server-computed dayOfEvent once the pulse lands.
  useEffect(() => {
    if (dayIdx != null) return
    if (pulse.data?.dayOfEvent != null) {
      setDayIdx(pulse.data.dayOfEvent - 1)
    }
  }, [pulse.data?.dayOfEvent, dayIdx])

  const totalDays = pulse.data?.totalDays ?? 1
  const eventName = event?.name ?? 'Live View'
  const dayLabelIdx = dayIdx ?? (pulse.data?.dayOfEvent ?? 1) - 1
  const dayLabelText = `Live · Day ${dayLabelIdx + 1} of ${totalDays}`

  const days = useMemo(() => {
    if (!event) return [] as Date[]
    const start = new Date(`${event.startDate}T00:00:00`)
    return Array.from({ length: totalDays }, (_, i) => {
      const d = new Date(start)
      d.setDate(d.getDate() + i)
      return d
    })
  }, [event, totalDays])

  const currentDay = days[dayLabelIdx]
  const calendarStamp = currentDay
    ? currentDay.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
    : ''
  const wallClockText = wallClock.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false
  })

  const connState = deriveConnState(pulse.secondsSinceUpdate, pulse.isError)
  const showLostConnectionBanner = connState === 'error'

  if (!validId) {
    return (
      <ShellChrome>
        <FallbackBody
          title="Live View"
          subtitle="That event identifier is invalid."
          onBack={() => navigate('/events')}
          backLabel="Back to Events"
        />
      </ShellChrome>
    )
  }
  if (eventLoading) {
    return (
      <ShellChrome>
        <FallbackBody title="Live View" subtitle="Loading event…" />
      </ShellChrome>
    )
  }
  if (eventError || !event) {
    return (
      <ShellChrome>
        <FallbackBody
          title="Live View"
          subtitle={eventError ? (eventError as Error).message : 'Event not found.'}
          onBack={() => navigate('/events')}
          backLabel="Back to Events"
        />
      </ShellChrome>
    )
  }

  const isAdmin = !!me?.isAdmin
  const onShiftCount = pulse.data
    ? pulse.data.shifts.filter((s) => s.signedOffAt == null).length
    : 0

  function onViewPerson(userId: number) {
    if (!pulse.data) return
    const shift = pulse.data.shifts.find((s) => s.userId === userId)
    if (shift) setViewingShift(shift)
  }

  return (
    <div className="day-view-shell" data-phone-view={phoneView}>
      {/* Header */}
      <div className="day-view-top">
        <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
          <button
            type="button"
            onClick={() => navigate(`/events/${eventId}`)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-md transition-colors"
            style={{
              background: 'rgba(245, 241, 234, 0.1)',
              border: '1px solid rgba(245, 241, 234, 0.2)',
              color: '#fdfbf6',
              padding: '7px 12px'
            }}
            aria-label="Exit Live View"
          >
            <Icon name="arrow-left" size={14} />
            Exit Live
          </button>
          <div style={{ minWidth: 0 }}>
            <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
              <span
                className="live-pulse-circle"
                aria-label="Live"
              />
              <span
                style={{
                  fontSize: 11,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  color: 'rgba(245, 241, 234, 0.7)'
                }}
              >
                {dayLabelText}
              </span>
              <span
                className="live-conn-dot"
                data-state={connState}
                aria-label={
                  connState === 'ok'
                    ? 'Connection healthy'
                    : connState === 'stale'
                      ? 'Connection stale'
                      : 'Connection lost'
                }
              />
              {pulse.secondsSinceUpdate != null && (
                <span
                  className="font-mono"
                  style={{ fontSize: 11, color: 'rgba(245, 241, 234, 0.5)' }}
                >
                  Updated {pulse.secondsSinceUpdate}s ago
                </span>
              )}
            </div>
            <div
              className="font-display"
              style={{ fontSize: 22, fontWeight: 600, color: '#fdfbf6', lineHeight: 1.1, marginTop: 2 }}
            >
              {eventName}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {isAdmin && (
            <div
              style={{
                display: 'flex',
                gap: 0,
                background: 'rgba(245, 241, 234, 0.08)',
                borderRadius: 7,
                padding: 3
              }}
            >
              <ViewToggleButton active={view === 'overview'} onClick={() => setView('overview')}>
                Manager Overview
              </ViewToggleButton>
              <ViewToggleButton active={view === 'mine'} onClick={() => setView('mine')}>
                My Duties
              </ViewToggleButton>
            </div>
          )}

          {/* Calendar date stamp + wall-clock — prototype L2386 */}
          {calendarStamp && (
            <div
              className="font-mono text-sm flex items-center gap-2"
              style={{ color: 'rgba(245, 241, 234, 0.7)' }}
            >
              <span>{calendarStamp}</span>
              <span
                aria-hidden
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background: 'rgba(245, 241, 234, 0.35)'
                }}
              />
              <span aria-label="Current time">{wallClockText}</span>
            </div>
          )}

          <HeaderButton onClick={() => setShowResources(true)} icon="book-open">
            Resources
          </HeaderButton>
          {isAdmin && (
            <HeaderButton onClick={() => setShowBroadcast(true)} icon="message-square">
              Broadcast
            </HeaderButton>
          )}
          <HeaderButton onClick={() => setPhoneView((p) => !p)} icon={phoneView ? 'home' : 'eye'}>
            {phoneView ? 'Desktop' : 'Mobile'}
          </HeaderButton>
          <button
            type="button"
            onClick={() => setShowAlarm(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-md"
            style={{
              background: '#7a2329',
              border: '1px solid #7a2329',
              color: '#fdfbf6',
              padding: '7px 12px'
            }}
          >
            <Icon name="siren" size={14} />
            Raise Alarm
          </button>
        </div>
      </div>

      {/* Lost-connection banner — sticky under the header when the pulse
          has stalled past 180s OR errored. Distinct from the body-level
          error block (which only shows on actual fetch errors). */}
      {showLostConnectionBanner && (
        <div
          role="alert"
          style={{
            background: 'rgba(122, 35, 41, 0.65)',
            borderBottom: '1px solid rgba(255, 100, 100, 0.5)',
            color: '#fdfbf6',
            padding: '10px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap'
          }}
        >
          <div className="flex items-center gap-2 text-sm">
            <Icon name="alert-triangle" size={14} />
            <span className="font-semibold">Lost connection</span>
            <span style={{ color: 'rgba(245, 241, 234, 0.75)' }}>
              {pulse.isError
                ? `— ${(pulse.error as Error | null)?.message ?? 'pulse fetch failed'}`
                : pulse.secondsSinceUpdate != null
                  ? `— last update ${formatStaleness(pulse.secondsSinceUpdate)} ago`
                  : ''}
            </span>
          </div>
          <button
            type="button"
            onClick={pulse.refetch}
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded"
            style={{
              background: 'rgba(245, 241, 234, 0.15)',
              border: '1px solid rgba(245, 241, 234, 0.3)',
              color: '#fdfbf6',
              padding: '6px 12px'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Day stepper — only for multi-day events */}
      {totalDays > 1 && (
        <div
          className="scrollbar-clean"
          style={{
            background: 'rgba(0, 0, 0, 0.15)',
            borderBottom: '1px solid rgba(245, 241, 234, 0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 24px',
            overflowX: 'auto'
          }}
        >
          {days.map((d, i) => {
            const active = i === dayLabelIdx
            return (
              <button
                key={i}
                type="button"
                onClick={() => setDayIdx(i)}
                style={{
                  background: active ? '#9b7f2a' : 'rgba(245, 241, 234, 0.08)',
                  color: active ? '#fff' : 'rgba(245, 241, 234, 0.7)',
                  border: `1px solid ${active ? '#9b7f2a' : 'rgba(245, 241, 234, 0.15)'}`,
                  padding: '8px 14px',
                  borderRadius: 7,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <div style={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Day {i + 1}
                </div>
                {d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
              </button>
            )
          })}
        </div>
      )}

      {/* Body */}
      <div className="day-view-content scrollbar-clean">
        {pulse.isLoading && (
          <div className="text-sm" style={{ color: 'rgba(245, 241, 234, 0.6)' }}>
            Loading live pulse…
          </div>
        )}
        {pulse.isError && !pulse.isLoading && (
          <div
            className="rounded-md p-4 text-sm"
            style={{
              background: 'rgba(122, 35, 41, 0.18)',
              border: '1px solid rgba(255, 100, 100, 0.3)',
              color: '#fdfbf6'
            }}
          >
            <div className="font-semibold mb-1">Could not load live pulse</div>
            <div style={{ color: 'rgba(245, 241, 234, 0.75)' }}>
              {(pulse.error as Error | null)?.message ?? 'unknown error'}
            </div>
            <button
              type="button"
              onClick={pulse.refetch}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold rounded"
              style={{
                background: 'rgba(245, 241, 234, 0.12)',
                border: '1px solid rgba(245, 241, 234, 0.25)',
                color: '#fdfbf6',
                padding: '6px 10px'
              }}
            >
              Retry
            </button>
          </div>
        )}
        {!pulse.isLoading && !pulse.isError && pulse.data && (
          <>
            {view === 'overview' && isAdmin && (
              <LiveManagerOverview
                eventId={eventId}
                pulse={pulse.data}
                canEdit={isAdmin}
                onViewPerson={onViewPerson}
                showToast={setToast}
              />
            )}
            {(view === 'mine' || !isAdmin) && (
              <LiveMyDuties
                eventId={eventId}
                pulse={pulse.data}
                meDbUserId={me?.dbUserId ?? null}
                onShowResources={() => setShowResources(true)}
                showToast={setToast}
              />
            )}
          </>
        )}
      </div>

      {/* Resources FAB — visible only in phone-view mode. */}
      {phoneView && (
        <button
          type="button"
          onClick={() => setShowResources(true)}
          className="live-resources-fab"
          aria-label="Open quick reference"
        >
          <Icon name="book-open" size={22} />
        </button>
      )}

      {/* Overlays */}
      {showAlarm && (
        <AlarmModal
          eventId={eventId}
          onClose={() => setShowAlarm(false)}
          onSent={() => setToast('Alarm sent — duty manager notified')}
        />
      )}
      {showBroadcast && (
        <BroadcastModal
          eventId={eventId}
          onShiftCount={onShiftCount}
          onClose={() => setShowBroadcast(false)}
          onSent={(count) => setToast(`Broadcast sent to ${count} on-shift staff`)}
        />
      )}
      {showResources && (
        <ResourcesPanel
          eventId={eventId}
          eventName={eventName}
          onClose={() => setShowResources(false)}
        />
      )}
      {viewingShift && (
        <PersonDutyModal
          shift={viewingShift}
          onClose={() => setViewingShift(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 24,
            transform: 'translateX(-50%)',
            background: 'rgba(20, 20, 20, 0.9)',
            color: '#fdfbf6',
            border: '1px solid rgba(155, 127, 42, 0.5)',
            borderRadius: 999,
            padding: '10px 18px',
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 14px 40px -10px rgba(0,0,0,0.55)',
            zIndex: 500
          }}
        >
          {toast}
        </div>
      )}
    </div>
  )
}

// ---- helpers ---------------------------------------------------------------

function ViewToggleButton({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        background: active ? '#9b7f2a' : 'transparent',
        color: active ? '#fff' : 'rgba(245, 241, 234, 0.8)',
        border: 'none',
        padding: '7px 14px',
        borderRadius: 5,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer'
      }}
    >
      {children}
    </button>
  )
}

function HeaderButton({
  onClick,
  icon,
  children
}: {
  onClick: () => void
  icon: React.ComponentProps<typeof Icon>['name']
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-md"
      style={{
        background: 'rgba(245, 241, 234, 0.1)',
        border: '1px solid rgba(245, 241, 234, 0.2)',
        color: '#fdfbf6',
        padding: '7px 12px'
      }}
    >
      <Icon name={icon} size={14} />
      {children}
    </button>
  )
}

function ShellChrome({ children }: { children: React.ReactNode }) {
  return <div className="day-view-shell">{children}</div>
}

function FallbackBody({
  title,
  subtitle,
  onBack,
  backLabel
}: {
  title: string
  subtitle: string
  onBack?: () => void
  backLabel?: string
}) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        textAlign: 'center'
      }}
    >
      <div className="font-display" style={{ fontSize: 26, fontWeight: 600, marginBottom: 10 }}>
        {title}
      </div>
      <p style={{ maxWidth: 480, lineHeight: 1.5, opacity: 0.85, marginBottom: 20 }}>{subtitle}</p>
      {onBack && backLabel && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-md"
          style={{
            background: 'rgba(245, 241, 234, 0.12)',
            border: '1px solid rgba(245, 241, 234, 0.3)',
            color: '#fdfbf6',
            padding: '8px 16px'
          }}
        >
          {backLabel}
        </button>
      )}
    </div>
  )
}

function formatStaleness(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  return `${hours}h ${mins % 60}m`
}
